<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { agingBucketLabel, formatMoney, toIsoDate } from '../utils/format'
import type {
  CurrencyOption,
  Option,
  PayablesAgingRow,
  PayablesAgingSummary,
} from '../types/finance'

const { t } = useI18n()
const { payablesAging, listCurrencies, listAllAgentsByRole } = useFinanceApi()

const PER_PAGE = 25

const rows = ref<PayablesAgingRow[]>([])
const summary = ref<PayablesAgingSummary | null>(null)
const currencies = ref<CurrencyOption[]>([])
const suppliers = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const page = ref(1)
const totalPages = ref(1)
const total = ref(0)

const filters = ref({
  as_of_date: toIsoDate(),
  supplier_id: '' as string | number,
  currency_code: '',
  status: '' as 'open' | 'partial' | 'overdue' | 'closed' | '',
})

const statuses = ['open', 'partial', 'overdue', 'closed'] as const

const buckets = computed(() => [
  { key: 'current', value: summary.value?.current ?? 0 },
  { key: '1_30', value: summary.value?.bucket_1_30 ?? 0 },
  { key: '31_60', value: summary.value?.bucket_31_60 ?? 0 },
  { key: '60_plus', value: summary.value?.bucket_60_plus ?? 0 },
])

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total.value)
  return t('finance.common.showing', { from, to, total: total.value })
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await payablesAging({
      as_of_date: filters.value.as_of_date,
      supplier_id: filters.value.supplier_id,
      currency_code: filters.value.currency_code,
      status: filters.value.status,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.rows
    summary.value = result.summary
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadOptions() {
  const [currencyResult, supplierResult] = await Promise.allSettled([
    listCurrencies(),
    listAllAgentsByRole('supplier'),
  ])
  currencies.value = currencyResult.status === 'fulfilled' ? currencyResult.value : []
  suppliers.value = supplierResult.status === 'fulfilled' ? supplierResult.value : []
}

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

watch(
  () => [filters.value.supplier_id, filters.value.currency_code, filters.value.status],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void loadOptions()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.payables.agingTitle') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="filters.as_of_date" class="date-input" type="date">
    <select v-model="filters.supplier_id" class="filter-select">
      <option value="">{{ t('finance.payables.supplier') }}</option>
      <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
        {{ supplier.name }}
      </option>
    </select>
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option v-for="status in statuses" :key="status" :value="status">
        {{ t(`finance.supplierInvoiceStatus.${status}`) }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
  </div>

  <div v-if="loading && rows.length === 0" class="state-message">
    <p>{{ t('finance.common.loading') }}</p>
  </div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.payables.totalPayables') }}</span>
        <span class="summary-value">{{ formatMoney(summary?.total_payables ?? 0) }}</span>
        <span class="summary-label">{{ t('finance.overview.asOf', { date: filters.as_of_date }) }}</span>
      </div>
      <div
        v-for="bucket in buckets"
        :key="bucket.key"
        class="summary-card"
        :class="{ attention: bucket.key === '60_plus' && bucket.value > 0 }"
      >
        <span class="summary-label">{{ agingBucketLabel(t, bucket.key) }}</span>
        <span class="summary-value">{{ formatMoney(bucket.value) }}</span>
      </div>
    </div>

    <div v-if="rows.length === 0" class="state-message">
      <p>{{ t('finance.payables.agingEmpty') }}</p>
    </div>

    <template v-else>
      <div class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th>{{ t('finance.payables.supplier') }}</th>
              <th>{{ t('finance.payables.invoiceNumber') }}</th>
              <th>{{ t('finance.payables.invoiceDate') }}</th>
              <th>{{ t('finance.common.dueDate') }}</th>
              <th>{{ t('finance.payables.overdueDays') }}</th>
              <th>{{ t('finance.payables.bucket') }}</th>
              <th class="cell-num">{{ t('finance.common.amount') }}</th>
              <th class="cell-num">{{ t('finance.receivables.paid') }}</th>
              <th class="cell-num">{{ t('finance.common.balance') }}</th>
              <th>{{ t('finance.common.status') }}</th>
              <th class="cell-actions">{{ t('finance.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.invoice_id">
              <td class="cell-name">{{ row.supplier_name }}</td>
              <td class="cell-code">{{ row.invoice_number }}</td>
              <td class="cell-date">{{ formatDisplayDate(row.invoice_date) }}</td>
              <td class="cell-date">{{ formatDisplayDate(row.due_date) }}</td>
              <td class="cell-num" :class="row.overdue_days > 0 ? 'is-negative' : ''">
                {{ row.overdue_days }}
              </td>
              <td>
                <span
                  class="status-badge"
                  :class="row.aging_bucket === 'current' ? 'status-ok' : row.aging_bucket === '60_plus' ? 'status-critical' : 'status-warn'"
                >
                  {{ agingBucketLabel(t, row.aging_bucket) }}
                </span>
              </td>
              <td class="cell-num">{{ formatMoney(row.invoice_amount, row.currency_code) }}</td>
              <td class="cell-num">{{ formatMoney(row.paid_amount, row.currency_code) }}</td>
              <td class="cell-num">{{ formatMoney(row.balance, row.currency_code) }}</td>
              <td>{{ t(`finance.supplierInvoiceStatus.${row.status}`) }}</td>
              <td class="cell-actions">
                <RouterLink class="link-action" :to="`/payables/invoices/${row.invoice_id}`">
                  {{ t('finance.common.open') }}
                </RouterLink>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <span>{{ rangeLabel }}</span>
        <button :disabled="page <= 1 || loading" @click="goToPage(page - 1)">
          {{ t('finance.common.previous') }}
        </button>
        <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
        <button :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">
          {{ t('finance.common.next') }}
        </button>
      </div>
    </template>
  </template>
</template>
